// @ts-check
/**
 * **テーマの決まり**（Issue #523。`duelEditor/core/` ＝ 舞台の決まりごと）。契約は `types.d.ts`。
 *
 * ## テーマ ＝ 舞台の見た目一式
 *
 * カスタマイズの単位は HUD 単体ではなく**盤と HUD をまとめた見た目**（ユーザー判断 2026-09-24）。
 * マットの絵とカードの見た目と HUD は同じ「見た目」の話で、別々に切り替えても意味がない。
 *
 * ## 器 / 決まり / 見た目 の 3 分割
 *
 * | | 中身 | 置き場 |
 * |---|---|---|
 * | 器 | 紙の大きさと解像度・投影・貼り付け・濃さの扱い・カメラ | `stage/` `canvas/` |
 * | 決まり | **盤の層が何であるか**・HUD の口の名前・文字の下限・検証 | `core/` |
 * | 見た目 | 層をどう描くか・HUD の場所と絵 | `themes/<name>/` |
 *
 * **盤の層の一覧は core が持つ**（ユーザー判断 2026-09-24）。テーマは描き方だけを持つので、
 * **濃さの設定がテーマをまたいで生きる**（テーマを切り替えてもスライダーはそのまま＝見比べられる）。
 */
import { effectKey, effectParts } from '../effect';
import { MARK_NAMES } from '../mark';
import { ANCHOR_LABELS, ANCHOR_PORT, HUD_PORT } from '../hud';
import { decorSchema, decorStyle } from '../decor';
import { transformSchema, transformStyle } from '../transform';

/** @typedef {import('./types').Theme} Theme */

/**
 * 層の**前提**。満たさない層は描かない。
 * **ここが語彙の一次情報**（綴り間違いを黙って「前提を満たす」にしないため）。
 */
export const LAYER_NEEDS = Object.freeze({
  video: (s) => Boolean(s?.video?.el) && s?.video?.size?.w > 0 && s?.video?.size?.h > 0,
  zones: (s) => Boolean(s?.field?.known),
  board: (s) => Boolean(s?.board),
});

/**
 * **盤の層**（この順に重なる。後のものが手前）。
 *
 * `presets` は見せ方ごとの既定の濃さ。真偽値ではなく 0〜1 で持つ（溶かして切り替えられるように）。
 * 層を足す・減らすのはここを変えることで、**全テーマに効く**。テーマ固有の絵を足したいだけなら
 * 既存の層の `draw` の中でやる。
 */
/*
 * **`needs` は前提であると同時に「フレームごとに変わるものをどれだけ読んでよいか」**（Issue #523）。
 * 盤は層ごとに紙（テクスチャ）を持ち、**その層が読むものが変わったときだけ描き直す**
 * （`stage/FieldLayer.js::layerDeps`）。`scene` でフレームごとに変わるのは `frameMs` と `board` なので、
 * `video` / `board` を宣言していない層がそれを読むと、絵が止まって見える。
 */
export const FIELD_LAYERS = Object.freeze([
  { name: 'matBack', label: 'マットの地', needs: [], presets: { live: 0, virtual: 1 } },
  { name: 'video', label: '実写動画', needs: ['video'], presets: { live: 1, virtual: 0 } },
  { name: 'matFront', label: 'マットの枠線', needs: ['zones'], presets: { live: 1, virtual: 1 } },
  { name: 'lines', label: 'ゾーンの枠', needs: ['zones'], presets: { live: 0, virtual: 0 } },
].map((l) => Object.freeze({ ...l, needs: Object.freeze(l.needs), presets: Object.freeze(l.presets) })));

/**
 * **盤に置かれているものの種類**（Issue #523。盤の層と同じ理屈で core が持つ）。
 *
 * 盤の層（`FIELD_LAYERS`）が「盤に印刷されているもの」なのに対し、こちらは
 * **オブジェクトの層**が描くもの ——「ocgCore の状態の描画」と「何を表示するかの設定」を持つ層。
 * 一覧を core が持つので、**テーマを切り替えても設定はそのまま生きる**。
 *
 * **`space` が「どこに置くか」**（Issue #523）:
 *
 * | space | 置き場 | 例 |
 * |---|---|---|
 * | `board` | **盤の上**（平面の座標。厚み・浮き・影を持てる。遠近が付く） | カード・山 |
 * | `screen` | **舞台のピクセル**（正射影。遠近は付かない） | 盤の縁・実写の手札 |
 *
 * **盤の縁（`frame`）がここに居る理由**（Issue #523。ユーザーと相談 2026-09-24）: ターンプレイヤーの色で
 * 光るので**盤に印刷されているもの（`field`）ではない**（`drawField` は紙で、その層が読むものが
 * 変わったときしか描き直さない＝状態を入れると設計ごと崩れる）。盤の**外**でもないので HUD でもない。
 * **core の状態から描くもの**＝この層。帯を持たないので演出（`effects`）でもない
 * （旧 `/fx` も `FieldFrame` は演出ファイルではなく舞台の部品）。
 *
 * 置き方を決めるのは器（`stage/ObjectLayer.js`）で、**テーマは見た目だけ**を返す。
 */
export const OBJECT_KINDS = Object.freeze([
  { name: 'card', label: 'カード', space: 'board', presets: { live: 1, virtual: 1 } },
  { name: 'pile', label: '山（デッキ・墓地・除外）', space: 'board', presets: { live: 1, virtual: 1 } },
  { name: 'frame', label: '盤の縁（ターンの色）', space: 'screen', presets: { live: 1, virtual: 1 } },
  // 実写の手札は**既定で出さない**（ユーザー判断 2026-09-25。出したいときは濃さを上げる）
  { name: 'liveHand', label: '実写の手札', space: 'screen', presets: { live: 0, virtual: 0 } },
].map((k) => Object.freeze({ ...k, presets: Object.freeze(k.presets) })));

/** 種類 → どこに置くか（`OBJECT_KINDS` の宣言から導く） */
export const OBJECT_SPACE = Object.freeze(Object.fromEntries(OBJECT_KINDS.map((k) => [k.name, k.space])));

export const OBJECT_KIND_NAMES = Object.freeze(OBJECT_KINDS.map((k) => k.name));

/** 種類ごとの既定の濃さ（**宣言から導く**） */
export const OBJECT_PRESETS = Object.freeze(Object.fromEntries(['live', 'virtual'].map((kind) => [
  kind, Object.freeze(Object.fromEntries(OBJECT_KINDS.map((k) => [k.name, k.presets[kind]]))),
])));

/** 種類ごとの濃さ（渡されなかった種類は実写向けの既定） */
export const resolveObjects = (objects) => ({ ...OBJECT_PRESETS.live, ...(objects ?? {}) });

/* ------------------------------------------------------------------ *
 *  部品（トランスフォームが付く単位）
 * ------------------------------------------------------------------ */

/**
 * **舞台の部品**（Issue #523。ユーザー要望 2026-09-24「汎用的にパラメータを外だししない？
 * よくある編集ソフトっぽく」）。
 *
 * 編集ソフト（After Effects など）のレイヤーは
 *
 * ```
 * レイヤー
 * ├ トランスフォーム … アンカーポイント / 位置 / スケール / 回転 / 不透明度  ← **どれにも付く**
 * └ <タイプ> のオプション … テキストなら 書体・大きさ、…                    ← **固有**
 * ```
 *
 * という形をしている。**「どれにも付く」ほうを core が持つ**ので、テーマは固有のものだけ宣言すればよく、
 * 位置・大きさ・回転・濃さの**名前と範囲は全テーマ・全層で同じ**になる。
 *
 * 部品の一覧は**層の宣言から導く**（`FIELD_LAYERS` / `OBJECT_KINDS` / `ANCHOR_PORT`）ので、
 * 層や種類を足すとトランスフォームも勝手に付いてくる。`space` の意味は `OBJECT_KINDS` と同じ。
 */
export const STAGE_PARTS = Object.freeze([
  // **舞台の背景**（Issue #565。実写の動画のまわり・仮想フィールドの後ろ。テーマの `background`）
  { part: 'background', label: '舞台の背景', space: 'screen' },
  ...FIELD_LAYERS.map((l) => ({ part: `field:${l.name}`, label: `盤 / ${l.label}`, space: 'board' })),
  ...OBJECT_KINDS.map((k) => ({ part: `objects:${k.name}`, label: k.label, space: k.space })),
  ...ANCHOR_PORT.map((name) => ({ part: `hud:${name}`, label: `HUD / ${ANCHOR_LABELS[name] ?? name}`, space: 'screen' })),
].map((p) => Object.freeze(p)));

/** 部品 → どこに置くか（`board` / `screen`） */
export const PART_SPACE = Object.freeze(Object.fromEntries(STAGE_PARTS.map((p) => [p.part, p.space])));

export const FIELD_LAYER_NAMES = Object.freeze(FIELD_LAYERS.map((l) => l.name));

export const FIELD_LAYER_LABELS = Object.freeze(Object.fromEntries(FIELD_LAYERS.map((l) => [l.name, l.label])));

/** 見せ方ごとの既定の濃さ（**層の宣言から導く**ので、層を足しても穴が開かない） */
export const OPACITY_PRESETS = Object.freeze(Object.fromEntries(['live', 'virtual'].map((kind) => [
  kind, Object.freeze(Object.fromEntries(FIELD_LAYERS.map((l) => [l.name, l.presets[kind]]))),
])));

const clamp01 = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/** 層ごとの濃さ（渡されなかった層は実写向けの既定） */
export const resolveLayers = (layers) => ({ ...OPACITY_PRESETS.live, ...(layers ?? {}) });

/**
 * 今そこに描かれる層の名前。**濃さ 0・前提を満たさない・テーマが描き方を持たない**層を落とす。
 * @param theme `toTheme()` の結果
 * @param scene そのフレームの素材
 * @param layers 層ごとの濃さ
 */
export const visibleLayers = (theme, scene, layers) => FIELD_LAYERS
  .filter((l) => typeof theme?.field?.[l.name] === 'function'
    && clamp01(layers?.[l.name]) > 0
    && l.needs.every((n) => LAYER_NEEDS[n](scene)))
  .map((l) => l.name);

/** 契約の口の一覧（`types.d.ts` の `Theme` と 1 対 1） */
export const THEME_PORT = Object.freeze(['name', 'label', 'field', 'objects', 'effects', 'hud', 'schema', 'style', 'options', 'parts', 'post', 'background', 'images']);

/**
 * **舞台の背景の既定**（Issue #565）。テーマが背景を持たなければ舞台の器（`canvas/Canvas.js`）の色のまま。
 * @param {any} spec テーマの `background(style)` が返したもの
 * @returns {{ image: string | null, color: string | null, focus: { x: number, y: number }, brightness: number, saturate: number } | null}
 */
export function resolveBackground(spec) {
  if (!spec || typeof spec !== 'object') return null;
  const image = typeof spec.image === 'string' && spec.image ? spec.image : null;
  const color = typeof spec.color === 'string' && spec.color ? spec.color : null;
  if (!image && !color) return null;
  const f = spec.focus ?? {};
  const unit = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5);
  // 明るさ・彩度（1 = そのまま）。背景を一歩引かせる（盤と HUD より暗く・色を抜く）のに使う
  const ratio = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(3, Math.max(0, v)) : 1);
  return { image, color, focus: { x: unit(f.x), y: unit(f.y) }, brightness: ratio(spec.brightness), saturate: ratio(spec.saturate) };
}

/* ------------------------------------------------------------------ *
 *  見た目の宣言（`schema`）と既定値（`style`）
 * ------------------------------------------------------------------ */

/**
 * **テーマが「触れるもの」を宣言する形**（Issue #523。ユーザー要望 2026-09-24）。
 *
 * 目的は**テーマを作る道具**: AI が初稿を書き → **人が GUI で数値を詰め** → その結果がテーマに残る。
 * UI を何で作るかの前に「**何が触れるか**」の宣言が要る（宣言が無ければ、どんな GUI でも
 * そこだけ手書きになる）。
 *
 * **HUD だけの仕組みにしない**（ユーザー判断）—— `drawField` / `drawObjects` / `drawHud`、
 * 今後の演出の層にも同じものが届く。層が増えても宣言と配り方は変えない。
 *
 * ```js
 * schema = {
 *   plateW:    { type: 'number', label: '枠の幅', min: 200, max: 900, step: 1, part: 'hud:plate' },
 *   lifeColor: { type: 'color',  label: 'LP の色', part: 'hud:life', css: '--life-color' },
 * }
 * style = { plateW: 470, lifeColor: '#ffffff' }   // **schema のキーをすべて持つ**
 * ```
 *
 * - `part` は**どの層のどの部品か**（`field:mat` / `objects:card` / `hud:life` …）。
 *   舞台で部品を押したときに開くフォルダがこれで決まる
 * - `css` があるものは**そのまま CSS カスタムプロパティになる**（DOM の層）。
 *   canvas の層（盤・オブジェクト）は `style` の値を描く側がそのまま読む
 * - **型は後から増やせる**（読む側は `type` で分岐するだけ）—— 将来キーフレームを足すときのため
 */
export const SCHEMA_TYPES = Object.freeze(['number', 'boolean', 'color', 'text', 'select']);

/** 宣言 1 つを検める（`toTheme` から。理由の分かる例外にする） */
function checkField(name, key, field, style) {
  if (!field || typeof field !== 'object') throw new Error(`theme ${name}: schema ${key} は object です`);
  if (!SCHEMA_TYPES.includes(field.type)) throw new Error(`theme ${name}: schema ${key} の型 ${field.type}（${SCHEMA_TYPES.join(' / ')}）`);
  // **宣言したのに既定が無い**のを弾く（GUI に出るのに動かない、が起きないように）
  if (!(key in style)) throw new Error(`theme ${name}: schema ${key} の既定値が style にありません`);
  if (field.type === 'select' && !Array.isArray(field.options)) throw new Error(`theme ${name}: schema ${key} は select なので options が要ります`);
}

const RESOLVED = new WeakMap();   // theme → (overrides | NO_OVERRIDES) → 重ねた値
const NO_OVERRIDES = Object.freeze({});

/**
 * テーマの既定値に上書きを重ねる（**知らないキーは落とす** ＝ 宣言に無いものは効かない）。
 * **同じテーマと同じ上書き（同じオブジェクト）なら同じ凍ったオブジェクトを返す** —— 層ごと・毎フレーム呼ばれ、
 * 返した値は下の `withDefaults` の鍵にもなる（作り直すと、そちらの使い回しも毎回外れる。2026-10-10 のプロファイル）
 */
export function resolveStyle(theme, overrides) {
  const base = theme?.style ?? {};
  const key = overrides && typeof overrides === 'object' ? overrides : NO_OVERRIDES;
  const byTheme = theme && typeof theme === 'object' ? (RESOLVED.get(theme) ?? new WeakMap()) : null;
  const hit = byTheme?.get(key);
  if (hit) return hit;
  const out = { ...base };
  Object.entries(overrides ?? {}).forEach(([k, v]) => { if (k in base && v !== undefined) out[k] = v; });
  Object.freeze(out);
  if (byTheme) { byTheme.set(key, out); RESOLVED.set(theme, byTheme); }
  return out;
}

/**
 * **既定値の表に見た目の値を重ねる関数**を作る（`withDefaults(() => [A, B])` は `(style) => ({ ...A, ...B, ...style })` と同じ値）。
 * **同じ style（同じオブジェクト）なら同じ凍ったオブジェクトを返す**: カード 1 枚・HUD の部品 1 つごとに毎フレーム呼ばれ、
 * style はテーマの全部の値を持つので、毎回コピーすると JS の 1〜2 割になる（2026-10-10 のプロファイル。Issue #767 の描く側の重さ）。
 * 返した値は書き換えない（凍っている）
 */
export function withDefaults(defaultsOf) {
  // 既定値の表は**最初に呼ばれたときに読む**（テーマのファイルは互いに import し合い、読み込みの途中ではまだ定義されていないことがある）
  let base = null;
  const memo = new WeakMap();
  return (style) => {
    if (!base) base = Object.freeze(Object.assign({}, ...[].concat(defaultsOf())));
    if (!style || typeof style !== 'object') return base;
    let v = memo.get(style);
    if (!v) { v = Object.freeze({ ...base, ...style }); memo.set(style, v); }
    return v;
  };
}

/**
 * 宣言のうち **`css` を持つものだけ** を CSS カスタムプロパティの表にする。
 * DOM の層（HUD・編集の層）の root に当てる。**canvas の層には効かない**ので、
 * そちらは描く側が `style` の値を直に読む。
 */
const CSS_VARS = new WeakMap();   // schema → style → 表（同じ組なら同じ表。HUD の層が毎フレーム呼ぶ）

export function cssVars(schema, style) {
  const bySchema = schema && typeof schema === 'object' && style && typeof style === 'object'
    ? (CSS_VARS.get(schema) ?? new WeakMap()) : null;
  const hit = bySchema?.get(style);
  if (hit) return hit;
  const out = {};
  Object.entries(schema ?? {}).forEach(([key, field]) => {
    if (!field?.css) return;
    const v = style?.[key];
    if (v !== undefined && v !== null) out[field.css] = typeof v === 'number' ? String(v) : v;
  });
  Object.freeze(out);
  if (bySchema) { bySchema.set(style, out); CSS_VARS.set(schema, bySchema); }
  return out;
}

/** 宣言を部品ごとに束ねる（GUI のフォルダ・舞台で押したときの絞り込み） */
export function schemaByPart(schema) {
  const out = {};
  Object.entries(schema ?? {}).forEach(([key, field]) => {
    const part = field?.part ?? 'その他';
    (out[part] ??= {})[key] = field;
  });
  return out;
}

const NAME = /^[a-z][a-z0-9_]*$/;

/**
 * テーマを作る。**どのテーマもここを通る**ので口が欠けない。
 *
 * @param {object} spec
 * @param {string} spec.name 一意の名前（ファイル・フォルダの名前と揃える）
 * @param {string} spec.label 画面に出す名前
 * @param {Record<string, Function>} spec.field 層の名前 → `draw(ctx, scene, config)`。**欠けている層は描かない**
 * @param {object} spec.hud `core/hud` の `toHud()` を通したもの
 * @param {Record<string, Function>} [spec.objects] 種類の名前 → 描き方（React の部品）。**欠けている種類は描かない**
 * @param {ReadonlyArray<import('../effect/types').Effect>} [spec.effects] **演出**（状況 × 役割と 1 対 1。
 *   `core/effect` の `defineEffect()` を通したもの。同じ状況・同じ役割を 2 回名乗ったら例外）
 * @param {object} [spec.options] テーマ固有の設定の既定
 * @param {Record<string, any>} [spec.schema] **触れるものの宣言**（GUI はここから作る）
 * @param {Record<string, any>} [spec.style] その既定値（`schema` のキーをすべて持つ）
 * @param {ReadonlyArray<{part: string, label?: string, space?: string, decor?: readonly string[]}>} [spec.parts]
 * @param {((style: any) => any) | null} [spec.background] **舞台の背景**（Issue #565）。`(style) => { image, color, focus } | null`。
 *   持たなければ器の色のまま。バリエーションの選択は `style`（`schema` の select）で持つ
 * @param {ReadonlyArray<string>} [spec.images] **盤の紙が描く同梱の画像の URL**（Issue #565）。ページが先に読み、
 *   読み終わったら `scene.imagesReady` が変わって紙が描き直される（読めていない間に焼くと絵が抜けたまま残る）
 * @param {((style: any) => any) | null} [spec.post] 演出の層の後処理（Issue #549）
 *   **このテーマだけが持つ部品**（枠の中の要素など）と、**飾りを名乗る部品**
 *   （`{ part: 'hud:life', decor: ['stroke'] }`。同じ名前なら `STAGE_PARTS` の部品に足される）
 * @returns {Theme}
 */
export function toTheme({ name, label, field, hud, objects = {}, effects = [], options = {}, schema = {}, style = {}, parts = [], post = null, background = null, images = [] }) {
  if (!NAME.test(String(name ?? ''))) throw new Error(`theme: name が不正です（${name}）`);
  if (!label) throw new Error(`theme ${name}: label が要ります`);
  if (post !== null && typeof post !== 'function') throw new Error(`theme ${name}: post は (style) => 設定 の関数です`);
  if (background !== null && typeof background !== 'function') throw new Error(`theme ${name}: background は (style) => { image, color } の関数です`);
  if (!Array.isArray(images) || images.some((u) => typeof u !== 'string')) throw new Error(`theme ${name}: images は URL の配列です`);
  if (!field || typeof field !== 'object') throw new Error(`theme ${name}: field（層の描き方の表）が要ります`);
  Object.entries(field).forEach(([key, draw]) => {
    // **知らない層の名前は弾く** —— 綴り間違いを黙って「描かない」にしない
    if (!FIELD_LAYER_NAMES.includes(key)) throw new Error(`theme ${name}: 知らない層 ${key}（${FIELD_LAYER_NAMES.join(' / ')}）`);
    if (typeof draw !== 'function') throw new Error(`theme ${name}: 層 ${key} の描き方が関数ではありません`);
  });
  if (!objects || typeof objects !== 'object') throw new Error(`theme ${name}: objects（種類の描き方の表）は object です`);
  Object.entries(objects).forEach(([key, draw]) => {
    /*
     * **知らない種類の名前は弾く**（綴り間違いを黙って「描かない」にしない）。
     * 盤面から出る種類（`OBJECT_KINDS`）に加えて、**演出が置く印**（`core/mark` の `MARKS`）も
     * 同じ表で描き方を持つ（生存はオブジェクトレイヤーが持つので扱いは同じ。Issue #534）。
     */
    if (!OBJECT_KIND_NAMES.includes(key) && !MARK_NAMES.includes(key)) {
      throw new Error(`theme ${name}: 知らない種類 ${key}（${[...OBJECT_KIND_NAMES, ...MARK_NAMES].join(' / ')}）`);
    }
    if (typeof draw !== 'function') throw new Error(`theme ${name}: 種類 ${key} の描き方が関数ではありません`);
  });
  if (!hud || HUD_PORT.some((k) => !(k in hud))) throw new Error(`theme ${name}: hud は core/hud の toHud() を通してください`);
  if (typeof options !== 'object') throw new Error(`theme ${name}: options は object です`);
  if (!schema || typeof schema !== 'object') throw new Error(`theme ${name}: schema は object です`);
  if (!style || typeof style !== 'object') throw new Error(`theme ${name}: style は object です`);
  if (!Array.isArray(parts)) throw new Error(`theme ${name}: parts は配列です`);
  /*
   * **演出は「イベント名 → 演出」の表ではなく配列**（Issue #529）。`defineEffect` が `situation` を持つので
   * 表にすると同じ綴りが 2 か所に出る。**同じ状況・同じ役割を 2 回名乗ったら例外**
   * （どちらが効くか分からないまま片方が死ぬので）。
   */
  if (!Array.isArray(effects)) throw new Error(`theme ${name}: effects は配列です`);
  const seenEvent = new Set();
  effects.forEach((e) => {
    if (!e?.situation || !Array.isArray(e.parts)) throw new Error(`theme ${name}: effects は core/effect の defineEffect() を通してください`);
    // 鍵は 状況の名前 ＋ 役割（`move_destroy` / `move_destroy_motion`。Issue #628）。motion と effect は同居できる
    const key = effectKey(e);
    if (seenEvent.has(key)) throw new Error(`theme ${name}: 同じ状況・同じ役割の演出が 2 つあります（${key}）`);
    seenEvent.add(key);
  });
  /*
   * **トランスフォームは自動で混ぜる**（Issue #523）。テーマは 1 行も書かなくてよく、
   * 書いても既定は core のものが勝つ（**共通のものは共通の名前と範囲で**）。
   * 先に混ぜるので、GUI では部品のフォルダの**頭にトランスフォームが並ぶ**。
   */
  /*
   * 同じ名前は**後から足したほうが勝つ**（`STAGE_PARTS` の部品に `decor` を足せる）。
   * 並びは `STAGE_PARTS` の順 → テーマが名乗った順。
   */
  const byPart = new Map();
  // **演出の部品も同じ扱い**（`fx:<event>/<層>`。lil-gui にノブが出る。Issue #526 / #528）
  [...STAGE_PARTS, ...effectParts(effects), ...parts].forEach((x) => {
    if (!x?.part) throw new Error(`theme ${name}: parts の要素には part が要ります`);
    byPart.set(x.part, { space: 'screen', ...(byPart.get(x.part) ?? {}), ...x });
  });
  const all = [...byPart.values()];
  /*
   * **共通のものを先に混ぜる**（トランスフォーム → 飾り → テーマ固有）。
   * GUI はこの順にフォルダの中を並べるので、編集ソフトと同じ見え方になる。
   */
  const fullSchema = { ...transformSchema(all), ...decorSchema(all), ...schema };
  const fullStyle = { ...transformStyle(all), ...decorStyle(all), ...style };
  Object.entries(fullSchema).forEach(([key, f]) => checkField(name, key, f, fullStyle));
  return Object.freeze({
    name, label, hud,
    field: Object.freeze({ ...field }),
    objects: Object.freeze({ ...objects }),
    /** 演出（`themes/<name>/effects/<situation>_<role>.js`。**状況 × 役割と 1 対 1**） */
    effects: Object.freeze([...effects]),
    schema: Object.freeze(fullSchema),
    style: Object.freeze(fullStyle),
    options: Object.freeze({ ...options }),
    /** 部品の一覧（**トランスフォームが付く単位**。GUI のフォルダと舞台の選択がこれで繋がる） */
    parts: Object.freeze(all.map((p) => Object.freeze(p))),
    /**
     * **演出の層の後処理**（Issue #549）。`(style) => { bloom, threshold, radius, chroma } | null`。
     * 値は `style` から引く（lil-gui で詰められる）。持たないテーマは後処理なし
     */
    post: post ?? (() => null),
    /** **舞台の背景**（Issue #565）。`(style) => { image, color, focus } | null`（`resolveBackground` で読む） */
    background: background ?? (() => null),
    /** 盤の紙が描く同梱の画像（ページが先に読む。Issue #565） */
    images: Object.freeze([...images]),
  });
}
